import React, { useState, useRef, useEffect } from "react";
import SpeedTest from "@cloudflare/speedtest";

// Use a more flexible interface that matches the actual library
interface TestResults {
  downloadMbps?: number;
  uploadMbps?: number;
  latency?: number;
  summary?: any;
  scores?: any;
  raw?: any;
}

// Speedometer Component
const Speedometer: React.FC<{
  value: number;
  max: number;
  label: string;
  color: string;
}> = ({ value, max, label, color }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ textAlign: "center", margin: "10px" }}>
      <div style={{ position: "relative", display: "inline-block" }}>
        <svg width="140" height="140" viewBox="0 0 140 140">
          {/* Background circle */}
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform="rotate(-90 70 70)"
          />
        </svg>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: "bold", color: color }}>
            {value.toFixed(1) ?? "-- --"}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>Mbps</div>
        </div>
      </div>
      <div style={{ marginTop: "8px", fontWeight: "600", color: "#333" }}>
        {label}
      </div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar: React.FC<{ progress: number; color: string }> = ({
  progress,
  color,
}) => {
  return (
    <div
      style={{
        width: "100%",
        height: "8px",
        backgroundColor: "#e0e0e0",
        borderRadius: "4px",
        overflow: "hidden",
        margin: "10px 0",
      }}
    >
      <div
        style={{
          width: `${progress}%`,
          height: "100%",
          backgroundColor: color,
          borderRadius: "4px",
          transition: "width 0.3s ease",
        }}
      />
    </div>
  );
};

const SpeedTestComponent: React.FC = () => {
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [results, setResults] = useState<TestResults | null>(null);
  const [liveDownload, setLiveDownload] = useState<number>(0);
  const [liveUpload, setLiveUpload] = useState<number>(0);
  const [livelatency, setLiveLatency] = useState<number>(0);

  const [progress, setProgress] = useState<number>(0);
  const engineRef = useRef<any>(null);

  // Color scheme
  const colors = {
    primary: "#6366f1",
    secondary: "#10b981",
    accent: "#f59e0b",
    background: "#f8fafc",
    card: "#ffffff",
    text: "#1e293b",
    border: "#e2e8f0",
  };
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .grecaptcha-badge {
        display: none !important;
        position: fixed !important;
        z-index: 9999 !important;
      }
    `;
    document.head.appendChild(style);
  }, []);
  // Convert bps to Mbps
  const bpsToMbps = (bps: number): number => {
    return bps / 1000000;
  };

  // Safe function to get bandwidth values
  const getDownloadBandwidth = (results: any): number => {
    return results?.getDownloadBandwidth?.() || 0;
  };

  const getUploadBandwidth = (results: any): number => {
    return results?.getUploadBandwidth?.() || 0;
  };
  const getUnloadedLatency = (results: any): number => {
    return results?.getUnloadedLatency?.() || 0;
  };

  const initializeEngine = () => {
    if (engineRef.current) {
      // Clean up existing engine
      engineRef.current.pause();
      engineRef.current = null;
    }

    // Initialize new engine
    engineRef.current = new SpeedTest({
      autoStart: false,
    });

    const engine = engineRef.current;

    // Set up event handlers (same as in useEffect)
    engine.onRunningChange = (running: boolean) => {
      setIsRunning(running);
      if (!running) {
        setProgress(100);
      }
    };

    engine.onResultsChange = ({ type }: { type: string }) => {
      if (!engine.isFinished && engine.results) {
        const currentResults = engine.results;
        const downloadBps = getDownloadBandwidth(currentResults);
        const uploadBps = getUploadBandwidth(currentResults);
        const unloadedlatency = getUnloadedLatency(currentResults);
        const downloadMbps = bpsToMbps(downloadBps);
        const uploadMbps = bpsToMbps(uploadBps);

        setLiveDownload(downloadMbps);
        setLiveUpload(uploadMbps);
        setLiveLatency(unloadedlatency);
        setProgress((prev) => Math.min(prev + 10, 90));

        setResults({
          downloadMbps,
          uploadMbps,
          latency: unloadedlatency,
          raw: currentResults,
        });
      }
    };

    engine.onFinish = (results: any) => {
      const downloadBps = getDownloadBandwidth(results);
      const uploadBps = getUploadBandwidth(results);
      const unloadedlatency = getUnloadedLatency(results);

      const finalResults: TestResults = {
        downloadMbps: bpsToMbps(downloadBps),
        uploadMbps: bpsToMbps(uploadBps),
        latency: unloadedlatency,
        summary: results.getSummary?.(),
        scores: results.getScores?.(),
      };

      setResults(finalResults);
      setProgress(100);
      console.log("Results:", JSON.stringify(results, null, 2));
    };

    engine.onError = (e: any) => {
      console.log("Speed Test Error:", e);
      setProgress(0);
    };

    return engine;
  };

  // Update useEffect
  useEffect(() => {
    initializeEngine();
  }, []);
useEffect(() => {
    // Notify parent iframe overlay that speed test is ready
    window.parent.postMessage({ type: 'SPEEDTEST_READY' }, '*');
  }, []);
  // Update handleStartTest
  const handleStartTest = () => {
    const engine = initializeEngine();

    // Reset states
    setResults(null);
    setLiveDownload(0);
    setLiveUpload(0);
    setProgress(0);
    setLiveLatency(0);

    // Start the test
    engine.play();
  };

  const getSpeedQuality = (speed: number) => {
    if (speed >= 100) return { text: "Excellent", color: "#10b981" };
    if (speed >= 50) return { text: "Good", color: "#f59e0b" };
    if (speed >= 25) return { text: "Fair", color: "#f97316" };
    if (speed === 0) return { text: "-- --", color: "#ef4444" };
    return { text: "Poor", color: "#ef4444" };
  };

  const downloadQuality = getSpeedQuality(liveDownload);
  const uploadQuality = getSpeedQuality(liveUpload);
  const handleClose = () => {
    // Send message to parent window to close the overlay
    if (window.parent !== window) {
      window.parent.postMessage({ type: "CLOSE_SPEED_TEST" }, "*");
    }
  };
  const [activeTab, setActiveTab] = useState<"MLab" | "opentest">("MLab");

  // const colors = { card: "#f0f0f0" };
  return (
    <div>
      {process.env.REACT_APP_ENVIORNMENT !== "PRODUCTION" && (
        <div
          style={{
            display: "flex",
            cursor: "pointer",
            gap: "1rem",
            backgroundColor: "#ffffff",
            alignItems: "center",
            justifyContent: "space-between", // distributes space between tabs and close button
            padding: "0 10px", // optional horizontal padding
          }}
        >
          {/* Tabs container */}
          <div style={{ display: "flex", gap: "1rem" }}>
            <div
              style={{
                padding: "10px 20px",
                borderBottom:
                  activeTab === "MLab"
                    ? "3px solid #083454"
                    : "3px solid transparent",
                fontWeight: activeTab === "MLab" ? "500" : "400",
              }}
              onClick={() => setActiveTab("MLab")}
            >
              MLab
            </div>
            <div
              style={{
                padding: "10px 20px",
                borderBottom:
                  activeTab === "opentest"
                    ? "3px solid #083454"
                    : "3px solid transparent",
                fontWeight: activeTab === "opentest" ? "500" : "400",
              }}
              onClick={() => setActiveTab("opentest")}
            >
              OpenTest
            </div>
          </div>

          {/* Close button */}
          <div
            style={{
              background: "blue",
              width: "30px",
              height: "30px",
              textAlign: "center",
              color: "#FFFFFF",
              fontFamily: "Mazzard",
              fontWeight: "600",
              cursor: "pointer",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={handleClose}
          >
            X
          </div>
        </div>
       )} 
      {activeTab === "MLab" &&
        process.env.REACT_APP_ENVIORNMENT !== "PRODUCTION" && (
          // <div>
          //   <div
          //     style={{
          //       fontFamily: "Mazzard",
          //       backgroundColor: colors.card,
          //       background: colors.card,
          //       height: "95vh",
          //       overflowY: "auto",
          //       flexDirection: "row",
          //       display: "flex",
          //     }}
          //   >
          //     <div
          //       style={{
          //         display: "flex",
          //         backgroundColor: colors.card,
          //         padding: "40px",
          //         height: !isRunning || !results ? "95vh" : "",
          //         width: "50%",
          //       }}
          //     >
          //       <div style={{ textAlign: "center" }}>
          //         <h1
          //           style={{
          //             fontSize: "2.5rem",
          //             fontWeight: "700",
          //             color: colors.text,
          //             marginBottom: "10px",
          //             background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
          //             WebkitBackgroundClip: "text",
          //             WebkitTextFillColor: "transparent",
          //           }}
          //         >
          //           Internet Speed Test
          //         </h1>
          //         <p
          //           style={{
          //             fontSize: "1.1rem",
          //             color: "#64748b",
          //             marginBottom: "30px",
          //             textAlign: "center",
          //           }}
          //         >
          //           Test your download and upload speeds in real-time
          //         </p>
          //         <button
          //           onClick={handleStartTest}
          //           disabled={isRunning}
          //           style={{
          //             padding: "16px 32px",
          //             fontSize: "18px",
          //             fontWeight: "600",
          //             backgroundColor: isRunning ? "#94a3b8" : colors.primary,
          //             color: "white",
          //             border: "none",
          //             borderRadius: "12px",
          //             cursor: isRunning ? "not-allowed" : "pointer",
          //             transition: "all 0.3s ease",
          //             boxShadow: isRunning
          //               ? "none"
          //               : "0 4px 14px 0 rgba(99, 102, 241, 0.3)",
          //             transform: isRunning ? "none" : "translateY(0)",
          //             minWidth: "200px",
          //           }}
          //           onMouseOver={(e) => {
          //             if (!isRunning) {
          //               e.currentTarget.style.transform = "translateY(-2px)";
          //               e.currentTarget.style.boxShadow =
          //                 "0 6px 20px rgba(99, 102, 241, 0.4)";
          //             }
          //           }}
          //           onMouseOut={(e) => {
          //             if (!isRunning) {
          //               e.currentTarget.style.transform = "translateY(0)";
          //               e.currentTarget.style.boxShadow =
          //                 "0 4px 14px rgba(99, 102, 241, 0.3)";
          //             }
          //           }}
          //         >
          //           {isRunning ? (
          //             <div
          //               style={{
          //                 display: "flex",
          //                 alignItems: "center",
          //                 justifyContent: "center",
          //                 gap: "8px",
          //               }}
          //             >
          //               <div
          //                 style={{
          //                   width: "16px",
          //                   height: "16px",
          //                   border: "2px solid transparent",
          //                   borderTop: "2px solid white",
          //                   borderRadius: "50%",
          //                   animation: "spin 1s linear infinite",
          //                 }}
          //               />
          //               Testing...
          //             </div>
          //           ) : (
          //             "Start Speed Test"
          //           )}
          //         </button>
          //         {isRunning && (
          //           <div style={{ marginBottom: "40px" }}>
          //             <div
          //               style={{
          //                 display: "flex",
          //                 justifyContent: "space-between",
          //                 marginBottom: "8px",
          //                 width: "100%",
          //               }}
          //             >
          //               <span
          //                 style={{
          //                   fontSize: "14px",
          //                   color: colors.text,
          //                   fontWeight: "600",
          //                 }}
          //               >
          //                 Test Progress
          //               </span>
          //               <span
          //                 style={{
          //                   fontSize: "14px",
          //                   color: colors.primary,
          //                   fontWeight: "600",
          //                 }}
          //               >
          //                 {progress}%
          //               </span>
          //             </div>
          //             <ProgressBar progress={progress} color={colors.primary} />
          //           </div>
          //         )}

          //         <div
          //           style={{
          //             display: "grid",
          //             gridTemplateColumns: "1fr 1fr",
          //             gap: "20px",
          //             marginTop: "30px",
          //           }}
          //         >
          //           <div
          //             style={{
          //               textAlign: "center",
          //               padding: "20px",
          //               backgroundColor: "white",
          //               borderRadius: "12px",
          //               border: `2px solid ${downloadQuality.color}20`,
          //             }}
          //           >
          //             <div
          //               style={{
          //                 fontSize: "14px",
          //                 color: "#64748b",
          //                 marginBottom: "8px",
          //               }}
          //             >
          //               Download Quality
          //             </div>
          //             <div
          //               style={{
          //                 fontSize: "18px",
          //                 fontWeight: "700",
          //                 color: downloadQuality.color,
          //               }}
          //             >
          //               {downloadQuality.text ? downloadQuality.text : "-- --"}
          //             </div>
          //           </div>
          //           <div
          //             style={{
          //               textAlign: "center",
          //               padding: "20px",
          //               backgroundColor: "white",
          //               borderRadius: "12px",
          //               border: `2px solid ${uploadQuality.color}20`,
          //             }}
          //           >
          //             <div
          //               style={{
          //                 fontSize: "14px",
          //                 color: "#64748b",
          //                 marginBottom: "8px",
          //               }}
          //             >
          //               Upload Quality
          //             </div>
          //             <div
          //               style={{
          //                 fontSize: "18px",
          //                 fontWeight: "700",
          //                 color: uploadQuality.color,
          //               }}
          //             >
          //               {uploadQuality.text ? uploadQuality.text : "-- --"}
          //             </div>
          //           </div>
          //         </div>
          //       </div>
          //     </div>
          //     <div
          //       style={{
          //         display: "flex",
          //         backgroundColor: colors.card,
          //         padding: "40px",
          //         height: !isRunning || !results ? "95vh" : "",
          //       }}
          //     >
          //       {results && !isRunning ? (
          //         <div
          //           style={{
          //             padding: "30px",
          //             backgroundColor: "#f1f5f9",
          //             borderRadius: "16px",
          //             border: `2px solid ${colors.border}`,
          //             width: "100%",
          //             marginRight: "0",
          //           }}
          //         >
          //           <h3
          //             style={{
          //               textAlign: "center",
          //               marginBottom: "30px",
          //               color: colors.text,
          //               fontSize: "1.5rem",
          //               fontWeight: "600",
          //             }}
          //           >
          //             🎉 Test Complete!
          //           </h3>

          //           {/* <div
          //       style={{
          //         display: "flex",
          //         justifyContent: "center",
          //         gap: "40px",
          //         flexWrap: "wrap",
          //         marginBottom: "30px",
          //       }}
          //     >
          //       <Speedometer
          //         value={results.downloadMbps || 0}
          //         max={200}
          //         label="Download"
          //         color={colors.primary}
          //       />
          //       <Speedometer
          //         value={results.uploadMbps || 0}
          //         max={200}
          //         label="Upload"
          //         color={colors.secondary}
          //       />
          //     </div> */}

          //           <div
          //             style={{
          //               display: "grid",
          //               gridTemplateColumns: "1fr 1fr",
          //               gap: "20px",
          //               marginBottom: "30px",
          //             }}
          //           >
          //             <div
          //               style={{
          //                 textAlign: "center",
          //                 padding: "20px",
          //                 backgroundColor: "white",
          //                 borderRadius: "12px",
          //                 boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
          //               }}
          //             >
          //               <div
          //                 style={{
          //                   fontSize: "14px",
          //                   color: "#64748b",
          //                   marginBottom: "8px",
          //                 }}
          //               >
          //                 Download Speed
          //               </div>
          //               <div
          //                 style={{
          //                   fontSize: "24px",
          //                   fontWeight: "700",
          //                   color: colors.primary,
          //                 }}
          //               >
          //                 {results.downloadMbps?.toFixed(2)} Mbps
          //               </div>
          //             </div>
          //             <div
          //               style={{
          //                 textAlign: "center",
          //                 padding: "20px",
          //                 backgroundColor: "white",
          //                 borderRadius: "12px",
          //                 boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
          //               }}
          //             >
          //               <div
          //                 style={{
          //                   fontSize: "14px",
          //                   color: "#64748b",
          //                   marginBottom: "8px",
          //                 }}
          //               >
          //                 Upload Speed
          //               </div>
          //               <div
          //                 style={{
          //                   fontSize: "24px",
          //                   fontWeight: "700",
          //                   color: colors.secondary,
          //                 }}
          //               >
          //                 {results.uploadMbps?.toFixed(2)} Mbps
          //               </div>
          //             </div>
          //             <div
          //               style={{
          //                 textAlign: "center",
          //                 padding: "20px",
          //                 backgroundColor: "white",
          //                 borderRadius: "12px",
          //                 boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
          //               }}
          //             >
          //               <div
          //                 style={{
          //                   fontSize: "14px",
          //                   color: "#64748b",
          //                   marginBottom: "8px",
          //                 }}
          //               >
          //                 Latency
          //               </div>
          //               <div
          //                 style={{
          //                   fontSize: "24px",
          //                   fontWeight: "700",
          //                   color: colors.secondary,
          //                 }}
          //               >
          //                 {results.latency?.toFixed(2)} ms
          //               </div>
          //             </div>
          //           </div>

          //           {/* Raw results in JSON format */}
          //           {/* <details>
          //       <summary
          //         style={{
          //           cursor: "pointer",
          //           marginBottom: "15px",
          //           fontSize: "16px",
          //           fontWeight: "600",
          //           color: colors.text,
          //           padding: "10px",
          //           backgroundColor: "white",
          //           borderRadius: "8px",
          //           border: `1px solid ${colors.border}`,
          //         }}
          //       >
          //         📋 Detailed Technical Results
          //       </summary>
          //       <pre
          //         style={{
          //           backgroundColor: "#1e293b",
          //           color: "#e2e8f0",
          //           padding: "20px",
          //           borderRadius: "12px",
          //           overflow: "auto",
          //           fontSize: "12px",
          //           border: `1px solid ${colors.border}`,
          //         }}
          //       >
          //         {JSON.stringify(results, null, 2)}
          //       </pre>
          //     </details> */}
          //         </div>
          //       ) : (
          //         <div
          //           style={{
          //             marginBottom: "40px",
          //             padding: "30px",
          //             backgroundColor: "#f1f5f9",
          //             borderRadius: "16px",
          //             border: `2px dashed ${colors.border}`,
          //           }}
          //         >
          //           <h3
          //             style={{
          //               textAlign: "center",
          //               marginBottom: "30px",
          //               color: colors.text,
          //               fontSize: "1.5rem",
          //               fontWeight: "600",
          //             }}
          //           >
          //             📊 Live Results
          //           </h3>

          //           <div
          //             style={{
          //               display: "flex",
          //               justifyContent: "center",
          //               gap: "40px",
          //               flexWrap: "wrap",
          //             }}
          //           >
          //             <Speedometer
          //               value={liveDownload}
          //               max={200}
          //               label="Download"
          //               color={colors.primary}
          //             />
          //             <Speedometer
          //               value={liveUpload}
          //               max={200}
          //               label="Upload"
          //               color={colors.secondary}
          //             />
          //           </div>
          //         </div>
          //       )}
          //     </div>

          //     <style>
          //       {`
          //   @keyframes spin {
          //     0% { transform: rotate(0deg); }
          //     100% { transform: rotate(360deg); }
          //   }
          // `}
          //     </style>
          //   </div>
          // </div>
          <div style={{ height: "89vh", width: "100%" }}>
            <iframe
              src="//www.measurementlab.net/p/ndt-ws.html"
              style={{
                width: "100%",
                height: "90vh",
                border: "none",
                alignItems: "center",
              }}
              title="OpenSpeedTest"
            ></iframe>
          </div>
        )}
      {activeTab === "opentest" &&
        process.env.REACT_APP_ENVIORNMENT !== "PRODUCTION" && (
          <div style={{ height: "89vh", width: "100%" }}>
            <iframe
              src="https://openspeedtest.com/speedtest"
              style={{ width: "100%", height: "90vh", border: "none" }}
              title="OpenSpeedTest"
            ></iframe>
          </div>
        )}{" "}
      {process.env.REACT_APP_ENVIORNMENT === "PRODUCTION" && (
        <div style={{ height: "100vh", width: "100%", overflow: "hidden" }}>
          {/* Header row with close button at the right end */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div></div> {/* Empty div to push close button to the right */}
            <div
              style={{
                background: "blue",
                width: "30px",
                height: "30px",
                textAlign: "center",
                color: "#FFFFFF",
                fontFamily: "Mazzard",
                fontWeight: "600",
                cursor: "pointer",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={handleClose}
            >
              X
            </div>
          </div>
          <iframe
            src="https://openspeedtest.com/speedtest"
            style={{
              width: "100%",
              height: "calc(100vh - 30px)",
              border: "none",
            }}
            title="OpenSpeedTest"
          ></iframe>
        </div>
      )}
    </div>
  );
};

export default SpeedTestComponent;
